import { useId } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { Locale } from 'date-fns';
import type { TFunction } from 'i18next';
import { DATE_BOUND_TODAY, isWithinDateBounds, resolveDateBounds } from '@/entities/brief';
import { resolveDateFnsLocale } from '@/shared/i18n/date-fns-locale';
import { DatePicker } from '@/shared/ui';

export interface DateFieldValue {
  start: string | null;
  end: string | null;
}

export interface DateFieldConfig {
  format?: string;
  minDate?: string | null;
  maxDate?: string | null;
}

/**
 * The template builder writes exactly one of two strings, and the server's `@IsIn` validates
 * them, so the range pair is gated on the range value rather than on "not the single value".
 * Gating the other way round made an absent or unrecognised format open the To input, which
 * captures an end date the field was never configured to hold.
 */
// eslint-disable-next-line i18next/no-literal-string -- wire value: DateSectionConfigDto validates this exact string server-side
const RANGE_FORMAT = 'Start + End date';

interface DateFieldProps {
  value: DateFieldValue | null;
  config: DateFieldConfig;
  disabled?: boolean;
  onChange: (next: DateFieldValue) => void;
}

/**
 * `DatePicker.Trigger` only checks for `null` before formatting, and `format()` on an Invalid
 * Date throws a RangeError. With no ErrorBoundary under this page that blanks the whole brief,
 * so an unparseable string degrades to "no date" here as well as at the Zod boundary: this is
 * the last point before the throw, and it must hold whatever route the value arrived by.
 */
const toDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed : null;
};
const toIso = (date: Date | null): string | null => (date ? format(date, 'yyyy-MM-dd') : null);

/** Same `PPP` form and locale the DatePicker trigger prints, so the caption reads like the picked date. */
function rangeCaption(
  config: DateFieldConfig,
  t: TFunction<'projects'>,
  locale: Locale,
): string | null {
  const { min, max } = resolveDateBounds({ minDate: config.minDate, maxDate: config.maxDate });
  const day = (date: Date) => format(date, 'PPP', { locale });
  const minIsToday = config.minDate === DATE_BOUND_TODAY;
  if (min && max) {
    return minIsToday
      ? t('projectBriefDetail.briefSectionField.dateField.rangeBetweenToday', { max: day(max) })
      : t('projectBriefDetail.briefSectionField.dateField.rangeBetween', {
          min: day(min),
          max: day(max),
        });
  }
  if (min) {
    return minIsToday
      ? t('projectBriefDetail.briefSectionField.dateField.rangeFromToday')
      : t('projectBriefDetail.briefSectionField.dateField.rangeFrom', { min: day(min) });
  }
  if (max) {
    return t('projectBriefDetail.briefSectionField.dateField.rangeUntil', { max: day(max) });
  }
  return null;
}

/**
 * Pure, controlled — no hooks beyond local i18n, no capability reads (those live in
 * `BriefSectionShell`). Only the range format renders a From/To pair, per the prototype and
 * the spec's date-range scenario; every other config renders a single input.
 *
 * `DatePicker.Trigger` doesn't forward `aria-label`, so each picker gets its own `id` +
 * visually-hidden `<label htmlFor>` for a real programmatic label association instead. The range
 * caption describes each trigger, so it is read with whichever picker has focus.
 *
 * Days outside the section's bounds are disabled, but a stored value outside them still shows:
 * an older brief keeps its answer, and the server only checks a date that changes.
 */
export function DateField({ value, config, disabled = false, onChange }: DateFieldProps) {
  const { t, i18n } = useTranslation('projects');
  const startId = useId();
  const endId = useId();
  const captionId = useId();
  const isRangeMode = config.format === RANGE_FORMAT;
  const start = value?.start ?? null;
  const end = value?.end ?? null;
  const bounds = resolveDateBounds({ minDate: config.minDate, maxDate: config.maxDate });
  const isOutOfBounds = (day: Date) => !isWithinDateBounds(day, bounds);
  const caption = rangeCaption(config, t, resolveDateFnsLocale(i18n.language));
  const describedBy = caption ? captionId : undefined;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div>
          <label htmlFor={startId} className="sr-only">
            {t('projectBriefDetail.briefSectionField.dateField.startDate')}
          </label>
          <DatePicker
            id={startId}
            value={toDate(start)}
            disabled={disabled}
            onChange={(next) => onChange({ start: toIso(next), end: isRangeMode ? end : null })}
          >
            <DatePicker.Trigger aria-describedby={describedBy} />
            <DatePicker.Content disabledDates={isOutOfBounds} />
          </DatePicker>
        </div>
        {isRangeMode && (
          <div>
            <label htmlFor={endId} className="sr-only">
              {t('projectBriefDetail.briefSectionField.dateField.endDate')}
            </label>
            <DatePicker
              id={endId}
              value={toDate(end)}
              disabled={disabled}
              onChange={(next) => onChange({ start, end: toIso(next) })}
            >
              <DatePicker.Trigger aria-describedby={describedBy} />
              <DatePicker.Content disabledDates={isOutOfBounds} />
            </DatePicker>
          </div>
        )}
      </div>
      {caption && (
        <p id={captionId} className="text-xs text-muted-foreground">
          {caption}
        </p>
      )}
    </div>
  );
}
